import {
  select,
  put,
  takeEvery,
  takeLatest,
  all,
  call
} from "redux-saga/effects";

import { requestMiddleware } from "helpers/api";
import {
  addUnit,
  getUnitsClassifications,
  getUnit,
  getUnits,
  downloadPointsExcel,
  putUnit,
  removeUnit
} from "services/api/units";
import { LocalStorageProvider } from "services/LocalStorageProvider";
import { actions } from "./ducks";
import { UnitTypes, LayerAlias } from "constant";
import features from "features";
import { downloadBlobFile } from "helpers/downloadBlobFile";

function* handleFetchUnits({ payload, request, setSuccess, setError }) {
  const { setPolygonUnitVisibility } = actions;

  const layersSavedState = LocalStorageProvider.projects.getProject({
    projectId: payload.params.projectId
  });

  function* onSuccess(res) {
    if (payload.onSuccess) yield payload.onSuccess(res);

    if (res.params.type === UnitTypes.POLYGON) {
      const visibilityActions = Object.entries(
        layersSavedState?.[LayerAlias.MANUAL]?.[UnitTypes.POLYGON] || {}
      ).map(([id, isVisible]) =>
        put(
          setPolygonUnitVisibility({
            projectId: payload.params.projectId,
            id,
            isVisible
          })
        )
      );

      yield all(visibilityActions);
    }
  }

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    onSuccess,
    isReturnRequestPayload: true
  });
}

function* fetchUnits({ payload }) {
  const request = getUnits;
  const { fetchUnitsSuccess: setSuccess, fetchUnitsError: setError } = actions;

  yield* handleFetchUnits({
    payload,
    request,
    setSuccess,
    setError
  });
}

function* fetchPointsExcel({ payload }) {
  const request = downloadPointsExcel;
  const {
    fetchPointsExcelSuccess: setSuccess,
    fetchPointsExcelError: setError
  } = actions;

  function* onSuccess(res) {
    yield call(downloadBlobFile, res.data, "points.xlsx");
    if (payload.onSuccess) yield payload.onSuccess(res);
  }

  yield* requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    onSuccess,
    isReturnRequestPayload: true,
    isReturnAllResponse: true
  });
}

function* fetchUnit({ payload }) {
  const request = getUnit;
  const { fetchUnitSuccess: setSuccess, fetchUnitError: setError } = actions;

  function* onSuccess(res) {
    if (payload.onSuccess) yield payload.onSuccess(res);

    yield put(
      features.map.actions.setPointDetails({
        ...res.data,
        alias: payload.params.alias
      })
    );
  }

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    onSuccess,
    isReturnRequestPayload: true
  });
}

function* watchPolygonVisibility({ payload }) {
  LocalStorageProvider.projects.setPolygonVisibilityState({
    projectId: payload.projectId,
    updatedState: {
      [payload.id]: payload.isVisible
    }
  });
}

function* watchToggleAllPolygonsVisibility({ payload }) {
  const { unitsCollection } = yield select((state) => state.unitsManager);
  const { setPolygonUnitVisibility } = actions;

  yield all(
    unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].units.map(({ id }) =>
      put(
        setPolygonUnitVisibility({
          projectId: payload.projectId,
          id,
          isVisible: payload.isVisible
        })
      )
    )
  );
}

function* createUnit({ payload }) {
  const request = addUnit;
  const {
    createUnitSuccess: setSuccess,
    createUnitError: setError,
    setPolygonUnitVisibility
  } = actions;

  function* onSuccess(res) {
    if (payload.onSuccess) yield payload.onSuccess(res);

    switch (res.params.type) {
      case UnitTypes.POLYGON:
        yield put(
          setPolygonUnitVisibility({
            projectId: payload.params.projectId,
            id: res.data.id,
            isVisible: true
          })
        );
        break;
      default:
        break;
    }
  }

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    onSuccess,
    isReturnRequestPayload: true
  });
}

function* updateUnit({ payload }) {
  const request = putUnit;
  const { updateUnitSuccess: setSuccess, updateUnitError: setError } = actions;

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    isReturnRequestPayload: true
  });
}

function* deleteUnit({ payload }) {
  const recentPoints = yield select(
    (state) =>
      state.unitsManager.unitsCollection[LayerAlias.MANUAL][UnitTypes.POINT]
        .units
  );

  const request = removeUnit;
  const { deleteUnitSuccess: setSuccess, deleteUnitError: setError } = actions;

  function* onSuccess(res) {
    if (payload.onSuccess) yield payload.onSuccess(res);

    const { projectId, alias, type, unitId } = res.params;
    const recentSameTypePoints = recentPoints.filter(
      (point) =>
        point.additional.object_type === res.data.additional?.object_type
    );

    const unitKey =
      recentSameTypePoints.length === 1
        ? res.data.additional.object_type
        : unitId;

    LocalStorageProvider.projects.deleteState({
      projectId,
      path: [alias, type],
      keyToDelete: unitKey
    });
  }

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    onSuccess,
    isReturnRequestPayload: true
  });
}

function* fetchUnitsClassifications({ payload }) {
  const request = getUnitsClassifications;

  const {
    fetchUnitsClassificationsSuccess: setSuccess,
    fetchUnitsClassificationsError: setError
  } = actions;

  yield requestMiddleware({
    ...payload,
    request,
    setSuccess,
    setError,
    isReturnRequestPayload: true
  });
}

function* updatePolygonCreationHistory({ payload }) {
  const recentHistory = yield select(
    (state) => state.unitsManager.creationPolygonHistory
  );

  const { updateCreationPolygonHistory } = actions;

  yield put(updateCreationPolygonHistory([...recentHistory, payload]));
}

export default function* watchSaga() {
  yield takeLatest(actions.createUnitRequest, createUnit);
  yield takeLatest(actions.fetchPointsExcelRequest, fetchPointsExcel);
  yield takeEvery(actions.fetchUnitsRequest, fetchUnits);
  yield takeEvery(actions.fetchUnitRequest, fetchUnit);
  yield takeLatest(actions.updateUnitRequest, updateUnit);
  yield takeLatest(actions.deleteUnitRequest, deleteUnit);
  yield takeLatest(
    actions.fetchUnitsClassificationsRequest,
    fetchUnitsClassifications
  );
  yield takeLatest(
    actions.updateCreationPolygonParameters,
    updatePolygonCreationHistory
  );
  yield takeLatest(actions.setPolygonUnitVisibility, watchPolygonVisibility);
  yield takeLatest(
    actions.setAllPolygonsVisibility,
    watchToggleAllPolygonsVisibility
  );
}
